// Multi-view modal for Job Description (Facility view)
// Reuses JobDescription.js logic with in-modal navigation to Profile and Details views

import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, ActivityIndicator } from 'react-native';
import { useQueryClient } from '@tanstack/react-query';
import WebModal from './WebModal';
import JobDescriptionContent from './JobDescriptionContent.web';
import ProfileContent from './ProfileContent.web';
import DetailsContent from './DetailsContent.web';
import { webColors, webSpacing, webBorderRadius } from '../../constants/webStyles';

const JobDescriptionModal = ({ visible, job, onClose, onEdit, onRefresh }) => {
  const queryClient = useQueryClient();

  // View state management
  const [currentView, setCurrentView] = useState('jobDescription');
  const [viewStack, setViewStack] = useState([]); // For back navigation
  const [viewData, setViewData] = useState(null); // Data for current view

  // Reset state when modal opens/closes
  useEffect(() => {
    if (visible) {
      setCurrentView('jobDescription');
      setViewStack([]);
      setViewData(null);
    }
  }, [visible]);

  // Navigate to a new view
  const navigateTo = useCallback((view, data = null) => {
    setViewStack(prev => [...prev, { view: currentView, data: viewData }]);
    setCurrentView(view);
    setViewData(data);
  }, [currentView, viewData]);

  // Go back to previous view
  const goBack = useCallback(() => {
    if (viewStack.length > 0) {
      const newStack = [...viewStack];
      const prev = newStack.pop();
      setCurrentView(prev.view);
      setViewData(prev.data);
      setViewStack(newStack);
    } else {
      handleClose();
    }
  }, [viewStack]);

  // Close modal and cleanup
  const handleClose = useCallback(() => {
    // Cleanup modal-specific queries
    queryClient.removeQueries({ queryKey: ['jobModal'] });
    queryClient.removeQueries({ queryKey: ['jobApplicationsModal'] });
    queryClient.removeQueries({ queryKey: ['doctorProfileModal'] });

    // Reset view state
    setCurrentView('jobDescription');
    setViewStack([]);
    setViewData(null);

    // Call parent onClose
    onClose?.();
  }, [queryClient, onClose]);

  // Get title based on current view
  const getTitle = () => {
    switch (currentView) {
      case 'jobDescription':
        return 'Job Application';
      case 'profile':
        return 'Applicant Profile';
      case 'details':
        return 'Job Description';
      default:
        return 'Job Application';
    }
  };

  // Get subtitle based on current view
  const getSubtitle = () => {
    switch (currentView) {
      case 'jobDescription':
        return job?.title;
      case 'profile':
        return viewData?.doctor?.first_name
          ? `${viewData.doctor.first_name} ${viewData.doctor.last_name || ''}`
          : viewData?.name || 'Doctor Profile';
      case 'details':
        return job?.clinic?.name || 'Facility';
      default:
        return null;
    }
  };

  if (!visible || !job) return null;

  return (
    <WebModal
      visible={visible}
      onClose={handleClose}
      title={getTitle()}
      subtitle={getSubtitle()}
      maxWidth={600}
      showBackButton={viewStack.length > 0}
      onBack={goBack}
    >
      {/* Job Description View */}
      {currentView === 'jobDescription' && (
        <JobDescriptionContent
          job={job}
          onViewProfile={(applicantData) => navigateTo('profile', applicantData)}
          onViewDetails={() => navigateTo('details', job)}
          onEdit={onEdit ? (jobData) => { handleClose(); onEdit(jobData); } : undefined}
          onClose={handleClose}
          onRefresh={onRefresh}
        />
      )}

      {/* Profile View */}
      {currentView === 'profile' && (
        <ProfileContent
          applicantData={viewData}
          jobData={job}
          onBack={goBack}
          onRefresh={onRefresh}
        />
      )}

      {/* Details View */}
      {currentView === 'details' && (
        <DetailsContent
          job={job}
        />
      )}
    </WebModal>
  );
};

export default JobDescriptionModal;
